import { useEffect } from "react";
import { RADAR_SALARY_UPGRADE_RATIO, type RadarAlertSummary } from "@winche/shared";
import { Badge } from "@winche/ui";
import { ContributionPlanner } from "./ContributionPlanner";
import { Block, EmptyState, ErrorMessage, LoadButton, LoadingBlock, Metric, MetricGrid, SectionHeader, SourceNote } from "../components/ui";
import type { AdminFailure } from "../lib/callable-error";
import { DataTable, type Column } from "../components/table";
import { num, usd, usdUnit } from "../lib/format";

export interface UserBillingStat {
  uid: string;
  displayName: string;
  tier: "free" | "pro";
  mode: "search" | "radar";
  status: "active" | "past_due" | "canceled" | "trialing";
  cancelAtPeriodEnd: boolean;
  monthlyRevenueUsd: number;
  aiCostUsd: number;
  marginUsd: number;
}

export interface AdminBillingStats {
  freeCount: number;
  proSearchCount: number;
  proRadarCount: number;
  pastDueCount: number;
  cancelAtPeriodEndCount: number;
  mrrUsd: number;
  listMrrUsd?: number;
  proSearchMrrUsd: number;
  proRadarMrrUsd: number;
  lifetimeAiCostUsd?: number;
  periodAiCostUsd?: number | null;
  period?: string;
  periodCompleteness?: "complete" | "incomplete" | "missing";
  netCollectionsUsd?: number | null;
  contributionUsd?: number | null;
  aiCostUsd: number;
  grossProfitUsd: number;
  grossMarginPct: number;
  costLines?: Array<{
    key: string;
    amountUsd: number | null;
    provenance: string;
    completeness: string;
    period: string;
  }>;
  /** Radar alerts per Radar user, this UTC month and the previous one (specs/12 §8). */
  radarAlerts?: { current: RadarAlertSummary; previous: RadarAlertSummary };
  users: UserBillingStat[];
}

interface Props {
  stats: AdminBillingStats | null;
  loading: boolean;
  error: AdminFailure | null;
  onLoad: () => void;
}

function ratio(n: number | null) {
  return n == null ? "—" : n.toFixed(2);
}

/**
 * Radar value: alerts about offers that beat the user's confirmed job (D3)
 * per Radar user per month. An alert is one push decision (run summary or a
 * single grade-A match); delivered = reached a device or waits for the
 * user's 09–20 h window.
 */
function RadarAlertsPanel({ current, previous }: { current: RadarAlertSummary; previous: RadarAlertSummary }) {
  return (
    <Block
      title="Radar value"
      hint={`Pushes about offers that beat the user's confirmed job (pay +${Math.round((RADAR_SALARY_UPGRADE_RATIO - 1) * 100)}%, higher seniority or a preferred work mode without paying less).`}
    >
      <MetricGrid cols={5}>
        <Metric label={`Alerts per Radar user · ${current.period}`} value={ratio(current.alertsPerSubscriber)} sub={`${previous.period}: ${ratio(previous.alertsPerSubscriber)}`} />
        <Metric
          label="Radar users alerted"
          value={`${num(current.alertedSubscribers)} / ${num(current.radarSubscribers)}`}
          tone={current.radarSubscribers > 0 && current.alertedSubscribers === 0 ? "bad" : "default"}
        />
        <Metric label="Alerts delivered" value={`${num(current.delivered)} / ${num(current.alerts)}`} hint="Reached a device or waits for the user's 09–20 h window." />
        <Metric label="Better offers announced" value={num(current.matches)} />
      </MetricGrid>
      <SourceNote>Totals include users who left Radar this month; the ratio divides by today&apos;s Radar headcount.</SourceNote>
    </Block>
  );
}

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
  trialing: "Trialing",
};

export function MonetizationSection({ stats, loading, error, onLoad }: Props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onLoad(); }, []);

  const userColumns: Column<UserBillingStat>[] = [
    { key: "user", header: "User", cell: (u) => u.displayName, sortValue: (u) => u.displayName.toLowerCase() },
    {
      key: "plan",
      header: "Plan",
      cell: (u) => (
        <Badge tone={u.tier === "pro" ? "brand" : "neutral"} size="sm">
          {u.tier === "pro" ? `PRO ${u.mode === "radar" ? "Radar" : "Search"}` : "Free"}
        </Badge>
      ),
      sortValue: (u) => `${u.tier}-${u.mode}`,
    },
    {
      key: "status",
      header: "Status",
      cell: (u) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <span className={u.status === "past_due" ? "text-danger-fg" : undefined}>{STATUS_LABELS[u.status] ?? u.status}</span>
          {u.cancelAtPeriodEnd && (
            <Badge tone="warning" size="sm">
              Canceling
            </Badge>
          )}
        </span>
      ),
      sortValue: (u) => u.status,
    },
    {
      key: "revenue",
      header: "List revenue",
      align: "right",
      cell: (u) => usd(u.monthlyRevenueUsd),
      sortValue: (u) => u.monthlyRevenueUsd,
      footer: usd(stats?.users.reduce((s, u) => s + u.monthlyRevenueUsd, 0)),
    },
    {
      key: "ai",
      header: "Lifetime AI",
      align: "right",
      cell: (u) => <span className="text-muted-foreground">{usdUnit(u.aiCostUsd)}</span>,
      sortValue: (u) => u.aiCostUsd,
      footer: usd(stats?.users.reduce((s, u) => s + u.aiCostUsd, 0)),
    },
    {
      key: "diff",
      header: "List revenue − lifetime AI",
      title: "Not a margin: a monthly list price minus all-time AI cost",
      align: "right",
      cell: (u) => usd(u.marginUsd),
      sortValue: (u) => u.marginUsd,
    },
  ];

  return (
    <div>
      <SectionHeader
        title="Monetization"
        subtitle="List MRR, observed AI cost and same-period contribution when cash events exist."
        action={<LoadButton loading={loading} loaded={stats !== null} onLoad={onLoad} />}
      />

      {error && <ErrorMessage failure={error} onRetry={onLoad} retrying={loading} />}
      {!stats && loading && <LoadingBlock metrics={5} rows={6} label="Loading billing stats" />}
      {!stats && !loading && !error && <EmptyState message="No billing stats loaded." />}

      {stats && (
        <>
          {/* KPIs and the Search/Radar split first (admin-15). */}
          <MetricGrid cols={4} className="mb-3">
            <Metric
              label="List MRR"
              value={usd(stats.listMrrUsd ?? stats.mrrUsd)}
              sub={`${usd(stats.proSearchMrrUsd)} Search · ${usd(stats.proRadarMrrUsd)} Radar`}
              hint="Catalog price × headcount, not cash."
            />
            <Metric
              label={`AI cost · ${stats.period ?? "month"}`}
              value={usd(stats.periodAiCostUsd)}
              sub={stats.periodCompleteness === "incomplete" ? "Month in progress" : stats.periodCompleteness === "missing" ? "Missing" : undefined}
            />
            <Metric label="Net collections (UTC month)" value={usd(stats.netCollectionsUsd)} />
            <Metric
              label="Contribution"
              value={usd(stats.contributionUsd)}
              tone={stats.contributionUsd != null && stats.contributionUsd < 0 ? "bad" : "default"}
              hint="Net collections minus same-period AI when both are known. Missing periods show —, not $0."
            />
          </MetricGrid>
          <MetricGrid cols={5} className="mb-8">
            <Metric label="Free" value={num(stats.freeCount)} />
            <Metric label="PRO Search" value={num(stats.proSearchCount)} />
            <Metric label="PRO Radar" value={num(stats.proRadarCount)} />
            <Metric label="Past due" value={num(stats.pastDueCount)} tone={stats.pastDueCount > 0 ? "bad" : "default"} />
            <Metric label="Canceling" value={num(stats.cancelAtPeriodEndCount)} tone={stats.cancelAtPeriodEndCount > 0 ? "warn" : "default"} />
          </MetricGrid>

          {stats.radarAlerts && <RadarAlertsPanel current={stats.radarAlerts.current} previous={stats.radarAlerts.previous} />}

          {stats.costLines && stats.costLines.length > 0 && (
            <Block title="Cost lines" hint={`Lifetime AI (${usd(stats.lifetimeAiCostUsd ?? stats.aiCostUsd)}) is not subtracted from monthly MRR.`}>
              <DataTable
                caption="Cost lines"
                rows={stats.costLines}
                rowKey={(l) => l.key}
                columns={[
                  { key: "line", header: "Line", cell: (l) => <span className="font-mono">{l.key}</span> },
                  { key: "amount", header: "Amount", align: "right", cell: (l) => usd(l.amountUsd), sortValue: (l) => l.amountUsd },
                  { key: "prov", header: "Provenance", cell: (l) => <span className="text-muted-foreground">{l.provenance}</span> },
                  {
                    key: "complete",
                    header: "Completeness",
                    cell: (l) => (
                      <Badge tone={l.completeness === "complete" ? "neutral" : "warning"} size="sm">
                        {l.completeness}
                      </Badge>
                    ),
                  },
                ]}
              />
            </Block>
          )}

          <Block title="Per user">
            <DataTable
              caption="Billing per user"
              columns={userColumns}
              rows={stats.users}
              rowKey={(u) => u.uid}
              defaultSort={{ key: "revenue", dir: "desc" }}
              maxHeight={640}
            />
          </Block>
        </>
      )}

      {/* Planning tool last: it is not live data (admin-15). */}
      <ContributionPlanner />
    </div>
  );
}
